/* global fbq */
/**
 *
 *  Spiritual Gangster landing page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 30/05/2024.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../state";

// Components
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import Button from "../Button/Button";
import PartnersScroller from "../Homepage/PartnersScroller/PartnersScroller";
import TrainerCard from "../Homepage/PartnersScroller/TrainerCard/TrainerCard";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Utils
import { clientWidth, getPartnerPictureUrl } from "../../utils";

// Icons
import ArrowLeftIcon from "../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../icons/ArrowRightIcon";
import WoSpiritualGangsterIcon from "../../icons/WoSpiritualGangsterIcon";

// Images
import spiritualGangsterLogo from "./spiritual-gangster-logo.png";
import spiritualGangsterLogo2x from "./spiritual-gangster-logo@2x.png";
import spiritualGangsterLogo3x from "./spiritual-gangster-logo@3x.png";
import spiritualGangsterHeroImg from "./spiritual-gangster-hero-img.jpg";
import spiritualGangsterHeroImg2x from "./spiritual-gangster-hero-img@2x.jpg";

import spiritualGangsterCoachProgram from "./spiritual-gangster-coach-program.jpg";
import spiritualGangsterCoachProgram2x from "./spiritual-gangster-coach-program@2x.jpg";
import spiritualGangsterCoachProgram3x from "./spiritual-gangster-coach-program@2x.jpg";
import spiritualGangsterCoachProgramMobile from "./spiritual-gangster-coach-program-mobile.jpg";
import spiritualGangsterCoachProgramMobile2x from "./spiritual-gangster-coach-program-mobile@2x.jpg";
import spiritualGangsterCoachProgramMobile3x from "./spiritual-gangster-coach-program-mobile@2x.jpg";

import Access1 from "./Access";
import Connect2 from "./Connect";
import Train3 from "./Train";

const images = [
  // lifestyle1,
  // lifestyle2,
  // lifestyle3,
  // lifestyle4,
  // lifestyle5,
  // lifestyle6,
  // lifestyle7,
  // lifestyle8,
];

const upcomingExperiencesSliderResponsiveSettings = [
  {
    breakpoint: 640,
    settings: {
      slidesToShow: 1,
      initialSlide: 1,
    },
  },
  {
    breakpoint: 960,
    settings: {
      slidesToShow: 2,
      initialSlide: 1,
    },
  },
  {
    breakpoint: 1180,
    settings: {
      slidesToShow: 2,
      initialSlide: 1,
    },
  },
  {
    breakpoint: 1400,
    settings: {
      slidesToShow: 2,
      slidesToScroll: 1,
      initialSlide: 1,
    },
  },
  {
    breakpoint: 1600,
    settings: {
      slidesToShow: 2,
      slidesToScroll: 1,
      initialSlide: 1,
    },
  },
  {
    breakpoint: 1920,
    settings: {
      slidesToShow: 2,
      slidesToScroll: 1,
      initialSlide: 1,
    },
  },
  {
    breakpoint: 2560,
    settings: {
      slidesToShow: 2,
      slidesToScroll: 1,
      initialSlide: 1,
    },
  },
  {
    breakpoint: 5000,
    settings: {
      slidesToShow: 2,
      slidesToScroll: 1,
      initialSlide: 1,
    },
  },
  { breakpoint: 10000, settings: "unslick" },
];

const SliderPrevArrowBig = ({ className, style, onClick }) => (
  <button
    className={`${className ? `${className} tbk-h-[48px] tbk-w-[48px]` : ""}`}
    style={style}
    onClick={onClick}
  >
    <ArrowLeftIcon className="tbk-inline" />
  </button>
);

const SliderNextArrowBig = ({ className, style, onClick }) => (
  <button
    className={`${className ? `${className} tbk-h-[48px] tbk-w-[48px]` : ""}`}
    style={style}
    onClick={onClick}
  >
    <ArrowRightIcon className="tbk-inline" />
  </button>
);

export default function SpiritualGangsterLandingPage() {
  const { theme } = useTheme();
  const {
    user,
    products,
    productsLoaded,
    availablePartners,
    startedEvents,
    upcomingEvents,
    // completedEvents,
    partnerLoaded,
    partnerSummary,
    loadUpcomingEvents,
    loadProducts,
    loadAvailablePartners,
    clearUpcomingEventsLoaded,
  } = useAppState();

  const [autoPlay, setAutoPlay] = useState(true);
  useState(null);

  const heroRef = React.useRef(null);

  useEffect(
    () => {
      loadUpcomingEvents(
        user && user.id,
        "UPCOMING,STARTED,UPCOMING_FULL",
        { sort: "start", page: 0, size: 8 },
        user ? user.region : undefined
      );
    },
    [user] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const isUserLoggedIn = !!(user && user.id);

  const trainerPictureUrl =
    partnerLoaded && getPartnerPictureUrl(partnerSummary);

  React.useEffect(() => {
    let params = {};

    if (!products || !products.length) {
      return;
    }

    const spiritualGangsterProduct = products.find((val) =>
      val.name.toLowerCase().includes("spiritual")
    );

    if (spiritualGangsterProduct) {
      params.productId = spiritualGangsterProduct.id;
    }

    loadAvailablePartners(params, user ? user.region : "US");
  }, [user, products]);

  React.useEffect(() => {
    if (!productsLoaded) {
      loadProducts(user ? user.region : "US");
    }

    // return () => clearAvailablePartnersLoaded();
  }, [user]);

  const slickInitialised = true;

  const futureEvents =
    startedEvents && startedEvents.length > 0
      ? [].concat(startedEvents, upcomingEvents)
      : upcomingEvents;

  return (
    <>
      <ScrollToTopOnMount />
      <Meta
        name="description"
        content={`On-demand, in person, personal training in Los Angeles.`}
      />
      <Header
        // behaviour="none"
        isFixed
        isLoggedIn={isUserLoggedIn}
        returnTo="/spiritual-gangster"
        heroRef={heroRef}
      />

      <div
        ref={heroRef}
        className="tbk-relative tbk-overflow-hidden tbk-bg-[#F5F0E1] tbk-pb-0 md:tbk-pb-0 lg:tbk-h-112"
      >
        <div className="tbk-mx-auto tbk-flex tbk-flex-col tbk-justify-evenly md:tbk-container md:tbk-grid md:tbk-grid-cols-2">
          <div className="lg:tbk-pr-0_ tbk-flex tbk-max-h-full tbk-grow tbk-flex-col tbk-pl-2 tbk-pr-2 md:tbk-block lg:tbk-pl-22">
            <img
              className="l:tbk-mt-7  tbk-mt-4 tbk-mb-4 tbk-w-85 lg:tbk-mb-2"
              src={spiritualGangsterLogo}
              srcSet={`${spiritualGangsterLogo} 1x, ${spiritualGangsterLogo2x} 2x, ${spiritualGangsterLogo2x} 3x`}
              alt="Spiritual Gangster logo"
            />
            <h1 className="tbk-text-epic-bold_ tbk-text-h2-black md:tbk-text-h2-black tbk-mb-2 tbk-text-primary lg:tbk-mb-2 lg:!tbk-leading-none lg:!tbk-leading-slight">
              Spiritual Gangster Method
            </h1>
            <p className="tbk-text-h3-subtitle_ tbk-text-bright lg:tbk-text-bright tbk-mb-4 tbk-grow tbk-text-primary lg:tbk-mb-7 ">
              powered by TruBe
            </p>
          </div>
          <img
            className="tbk-top-7 tbk-bottom-0 tbk-left-1/2 tbk-right-0 tbk-h-full tbk-w-auto tbk-max-w-full tbk-object-cover lg:tbk-absolute"
            src={spiritualGangsterHeroImg}
            srcSet={`${spiritualGangsterHeroImg} 1x, ${spiritualGangsterHeroImg2x} 2x`}
            alt="Spiritual Gangster"
          />
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-4 tbk-pb-4 lg:tbk-pt-9 lg:tbk-pb-9">
        <div className="tbk-container tbk-px-2 lg:tbk-px-2">
          <div className="xl:tbk-grid-cols- lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-gap-8">
            <div className="tbk-mb-5 lg:tbk-mb-0">
              <p className="tbk-text-main tbk-mb-3 tbk-text-primary lg:tbk-mb-7">
                Introducing the Spiritual Gangster Coach Program powered by
                TruBe, elevating wellness with a holistic approach. The SG coach
                program encompasses yoga, meditation, sound healing, and
                breathwork, infused with Spiritual Gangster’s unique holistic
                wellness method. Through the TruBe Lifestyle Collective guests
                will enjoy monthly in-person group classes curated by SG. In
                addition, guests will have the opportunity to access
                personalized one-on-one sessions with SG coaches at exclusive
                discounts, along with special offers on Spiritual Gangster’s
                active and loungewear products.
              </p>
              <div className="tbk-text-center lg:tbk-text-left">
                <Link to="/partners/spiritual-gangster">
                  <Button
                    variant="secondary"
                    size={"md"}
                    endIcon={<ArrowRightIcon />}
                    className="tbk-w-56"
                  >
                    Book a Coach
                  </Button>
                </Link>
              </div>
            </div>
            <div>
              <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl tbk-pt-full md:tbk-pt-0 lg:tbk-h-auto lg:tbk-bg-none">
                <picture>
                  <source
                    media="(min-width: 768px)"
                    srcSet={`${spiritualGangsterCoachProgram} 1x, ${spiritualGangsterCoachProgram2x} 2x, ${spiritualGangsterCoachProgram3x} 3x`}
                  />
                  <source
                    srcSet={`${spiritualGangsterCoachProgramMobile} 1x, ${spiritualGangsterCoachProgramMobile2x} 2x`}
                  />
                  <img
                    className="tbk-absolute tbk-top-0 tbk-left-0 tbk-h-full tbk-w-full tbk-max-w-full tbk-object-cover md:tbk-relative"
                    role="presentation"
                    src={`${spiritualGangsterCoachProgram}`}
                    alt="Spiritual Gangster Coach Program"
                  />
                </picture>
              </div>
            </div>
          </div>
        </div>
      </div>

      {futureEvents.length ? (
        <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-4 tbk-pb-4 lg:tbk-pt-9">
          <div className="tbk-container tbk-px-3 lg:tbk-px-8 ">
            <div className="tbk-relative tbk-mb-3 tbk-text-center lg:tbk-mb-4">
              <h3 className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
                TruBe Lifestyle x<br className="md:tbk-hidden" /> Spiritual
                Gangster <br className="md:tbk-hidden" />
                Experiences
              </h3>
              <hr className="tbk-mb-6 tbk--mt-5.5 tbk-border-blue-grey-light md:tbk-mb-4 md:tbk--mt-2" />
            </div>
            <div className="tbk-flex tbk-justify-center">
              <div className="tbk-w-144">
                <p
                  className={
                    "tbk-text-main lg:tbk-text-small tbk-mb-7 tbk-text-center tbk-text-blue-grey"
                  }
                >
                  The TruBe lifestyle will introduce curated group events in
                  collaboration with Spiritual Gangster, offering a monthly
                  series showcasing various modalities including Yoga,
                  Meditation, Breathwork, and Sound Healing led by the TruBe x
                  Spiritual Gangster coaches
                </p>
              </div>
            </div>
            <div className="tbk-mb-7_ ">
              {slickInitialised && (
                <Slider
                  className="future-events-carousel tbk--ml-1 tbk--mr-1 tbk-pb-3 tbk-text-center"
                  dots={true}
                  dotsClass={
                    "slick-dots !tbk-inline-block tbk--translate-x-1/2 tbk-left-1/2 tbk-whitespace-nowrap"
                  }
                  arrows={clientWidth() > theme.responsive.breakpoints.lg}
                  autoplay={autoPlay}
                  autoplaySpeed={4000}
                  pauseOnFocus
                  pauseOnDotsHover
                  pauseOnHover
                  infinite={true}
                  speed={500}
                  slidesToShow={2}
                  slidesToScroll={1}
                  initialSlide={0}
                  responsive={upcomingExperiencesSliderResponsiveSettings}
                  nextArrow={<SliderNextArrowBig />}
                  prevArrow={<SliderPrevArrowBig />}
                  // onSwipe={handleSlideSwipe}
                >
                  {futureEvents.map((evt, index) => (
                    <div
                      id={`slide-${index}`}
                      className="slide upcoming-experiences-item tbk-pl-1 tbk-pr-1"
                      key={index}
                      // onClick={() => openGalleryCarouselModal(index)}
                    >
                      <div className="content slide-bg tbk-h-full tbk-w-full">
                        <Link
                          to={`/events/${evt.id}`}
                          // href={`https://trubeapp.com/app-redirects/deeplink-redirect-events.html?id=${evt.id}`}
                          // target="_blank"
                          className="tbk-mb-2 lg:tbk-mb-0"
                          onClick={clearUpcomingEventsLoaded}
                        >
                          <div className="tbk-relative">
                            <img
                              className="tbk-mb-2 tbk-h-48 tbk-w-full tbk-overflow-hidden tbk-rounded-2xl tbk-object-cover tbk-object-left-top lg:tbk-h-76"
                              src={evt.coverPhoto}
                              //srcSet={`${access2xImg} 2x, ${access3xImg} 3x`}
                              alt="Access"
                            />
                            <Button
                              variant="secondary"
                              size="md"
                              className="tbk-w-full"
                              endIcon={<ArrowRightIcon />}
                            >
                              RSVP
                            </Button>
                            {/*  <HowItWorks1 className="tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />*/}
                            {/*  <div className="tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-center tbk-text-basic-white">*/}
                            {/*    Access*/}
                            {/*  </div>*/}
                            {/*</div>*/}
                            {/*<div className="tbk-text-h3-subtitle tbk-px-3 tbk-pt-2 tbk-pb-1 tbk-text-center tbk-text-primary">*/}
                            {/*  Access and book the best coaches near you*/}
                          </div>
                        </Link>
                      </div>
                    </div>
                  ))}
                </Slider>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {availablePartners && availablePartners.length ? (
        <div className="tbk-relative tbk-bg-basic-white tbk-pt-7 tbk-pb-7 lg:tbk-pt-9 lg:tbk-pb-0">
          <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-2 lg:tbk-pb-5">
            <div className="tbk-mb-2 tbk-flex tbk-justify-center">
              <WoSpiritualGangsterIcon className="tbk-text-blue-grey" />
            </div>
            <div className="tbk-relative tbk-mb-2 tbk-text-center lg:tbk-mb-5">
              <h3 className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-mb-2 tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
                Spiritual Gangster Coaches
              </h3>
              <div className="tbk-flex tbk-justify-center">
                <div className="lg:tbk-w-100">
                  <p className="tbk-text-small lg:tbk-text-small tbk-text-blue-grey">
                    Each coach was meticulously selected and underwent a
                    rigorous onboarding and training process. Their coaching
                    approach is designed to blend the mind and body into a
                    holistic journey of wellness.
                  </p>
                </div>
              </div>
              {/*<hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />*/}
            </div>

            <div className="tbk--mx-2 tbk-mb-4 lg:tbk-mb-6">
              {availablePartners && availablePartners.length ? (
                <PartnersScroller
                  partners={availablePartners}
                  renderPartner={({
                    info: partnerInfo,
                    index,
                    contentScrollerApi,
                    onCardVisibleChange,
                  }) => (
                    <Link to={`/partners/${partnerInfo.id}`}>
                      <TrainerCard
                        trainer={partnerInfo}
                        key={partnerInfo.id}
                        itemKey={index}
                        contentScrollerApi={contentScrollerApi}
                        onCardVisibleChange={onCardVisibleChange}
                        // onCardClick={handleCardClick}
                      />
                    </Link>
                  )}
                />
              ) : null}
            </div>
            {/*<p className="tbk-text-main tbk-mb-4 tbk-px-2 tbk-text-center tbk-text-primary lg:tbk-hidden">*/}
            {/*  Each coach was meticulously selected and underwent a rigorous*/}
            {/*  onboarding and training process. Their coaching approach is*/}
            {/*  designed not only to help you achieve your fitness goals but also*/}
            {/*  reach your optimal health levels by infusing personalized*/}
            {/*  Cymbiotika products into your daily routine.*/}
            {/*</p>*/}
            <Link
              className="tbk-block tbk-text-center"
              to={
                isUserLoggedIn || true
                  ? "/partners/spiritual-gangster"
                  : "/become-a-member"
              }
            >
              <Button
                variant="secondary"
                className="tbk-inline-flex tbk-w-full tbk-pl-5 tbk-pr-5 lg:tbk-w-66"
                endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
              >
                View The Coaches
              </Button>
            </Link>
          </div>
        </div>
      ) : null}

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-7 tbk-pb-7 lg:tbk-pt-4 lg:tbk-pb-9">
        <div className="tbk-container tbk-mx-auto tbk-px-3 lg:tbk-px-2">
          <div className="tbk-relative tbk-mt-0 tbk-mb-6 tbk-text-center">
            <h3 className="tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-blue-grey">
              How it works
            </h3>
            <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
          </div>
          <div className="tbk-mb-7 md:tbk-grid md:tbk-grid-cols-2 md:tbk-gap-5 lg:tbk-grid-cols-3 lg:tbk-pt-3">
            <div className="tbk-mb-5 tbk-rounded-2xl tbk-border tbk-border-blue-grey-light lg:tbk-mb-0">
              <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                <div className="tbk-h-58" />
                <Access1 className="tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                <div className="tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-[#D8B888]">
                  Access
                </div>
              </div>
              <div className="tbk-text-main lg:tbk-text-h3-subtitle tbk-px-2 tbk-pt-2 tbk-pb-3 tbk-text-center tbk-text-primary lg:tbk-text-left">
                Access Your Spiritual Gangster coach in your area by inputting
                your desired in-person session location.
              </div>
            </div>
            <div className="tbk-mb-5 tbk-rounded-2xl tbk-border tbk-border-blue-grey-light lg:tbk-mb-0">
              <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                <div className="tbk-h-58" />
                <Connect2 className="tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                <div className="tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-[#D8B888]">
                  Connect
                </div>
              </div>
              <div className="tbk-text-main lg:tbk-text-h3-subtitle tbk-px-2 tbk-pt-2 tbk-pb-3 tbk-text-center tbk-text-primary lg:tbk-text-left">
                Book your coach and connect before the session via text message
                to personalize your in-person session and determine your goals.
              </div>
            </div>

            <div className="tbk-mb-5 tbk-rounded-2xl tbk-border tbk-border-blue-grey-light lg:tbk-mb-0">
              <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                <div className="tbk-h-58" />
                <Train3 className="tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                <div className="tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-[#D8B888]">
                  Train
                </div>
              </div>
              <div className="tbk-text-main lg:tbk-text-h3-subtitle tbk-px-2 tbk-pt-2 tbk-pb-3 tbk-text-center tbk-text-primary lg:tbk-text-left">
                Train 1:1 with continuous feedback and analysis to your goals
                and product results.
              </div>
            </div>
          </div>
          <Link
            className="tbk-block tbk-text-center"
            to={
              isUserLoggedIn || true
                ? "/partners/spiritual-gangster"
                : "/become-a-member"
            }
          >
            <Button
              variant="secondary"
              className="tbk-inline-flex tbk-w-66 tbk-pl-5 tbk-pr-5"
              endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
            >
              Start now
            </Button>
          </Link>
        </div>
      </div>

      <Footer />
    </>
  );
}
